import React, { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// A strip of picture thumbnails that shows `perPage` of them at a time, with buttons for the next and previous
// ones. Down the side on large screens, along the bottom on small ones.
// renderThumb(item, index, isSelected) draws one thumbnail, so each page keeps its own look.
function ThumbStrip({ items, selected, onSelect, renderThumb, perPage = 5, gap = 8, thumbSize = 54 }) {
  const [start, setStart] = useState(0);

  // keep the chosen picture inside the visible page
  useEffect(() => {
    setStart((s) => {
      if (selected < s) return selected;
      if (selected >= s + perPage) return selected - perPage + 1;
      return s;
    });
  }, [selected, perPage]);

  const maxStart = Math.max(0, items.length - perPage);
  const first = Math.min(start, maxStart);
  const visible = items.slice(first, first + perPage);
  const pager = items.length > perPage;
  const move = (dir) => setStart(Math.max(0, Math.min(maxStart, first + dir * perPage)));

  const arrowSx = {
    flexShrink: 0,
    width: { xs: 28, md: thumbSize },
    height: { xs: thumbSize, md: 24 },
    borderRadius: "6px",
    border: "1px solid #ddd",
    color: "#333",
    bgcolor: "#fff",
    "&:hover": { bgcolor: "#faf3e0", borderColor: "#C8A054" },
    "&.Mui-disabled": { opacity: 0.35 },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: "center", gap: `${gap}px`, flexShrink: 0, justifyContent: { xs: "center", md: "flex-start" } }}>
      {pager && (
        <IconButton aria-label="Previous pictures" size="small" disabled={first <= 0} onClick={() => move(-1)} sx={arrowSx}>
          <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}><KeyboardArrowUpIcon fontSize="small" /></Box>
          <Box component="span" sx={{ display: { xs: "inline-flex", md: "none" } }}><KeyboardArrowLeftIcon fontSize="small" /></Box>
        </IconButton>
      )}

      <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: `${gap}px`, minWidth: 0 }}>
        {visible.map((item, i) => {
          const index = first + i;
          return (
            <Box key={index} onClick={() => onSelect(index)} onMouseEnter={() => onSelect(index)} sx={{ display: "flex" }}>
              {renderThumb(item, index, index === selected)}
            </Box>
          );
        })}
      </Box>

      {pager && (
        <IconButton aria-label="Next pictures" size="small" disabled={first >= maxStart} onClick={() => move(1)} sx={arrowSx}>
          <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}><KeyboardArrowDownIcon fontSize="small" /></Box>
          <Box component="span" sx={{ display: { xs: "inline-flex", md: "none" } }}><KeyboardArrowRightIcon fontSize="small" /></Box>
        </IconButton>
      )}
    </Box>
  );
}

export default ThumbStrip;
